import { GlassCard, GlassCardContent, GlassCardHeader } from "@/components/ui/glass-card";
import { Badge } from "@/components/ui/badge";
import { Role } from "@/types/rbac";
import { Shield, Eye, Users, Crown, Lock, Activity } from "lucide-react";
import { ActionsDropdown } from "@/components/ui/actions-dropdown";
import { buildRoleActions } from "@/components/actions";

interface RoleTemplateCardProps {
  role: Role;
  userCount?: number;
  onEdit?: () => void;
  onDelete?: () => void;
  onView: () => void;
  canEdit?: boolean;
  canDelete?: boolean;
}

export default function RoleTemplateCard({ 
  role, 
  userCount = 0, 
  onEdit, 
  onDelete, 
  onView, 
  canEdit = true, 
  canDelete = true 
}: RoleTemplateCardProps) {
  const getRoleIcon = (roleName: string) => {
    const name = roleName.toLowerCase();
    if (name.includes('admin') || name.includes('super')) {
      return <Crown className="h-6 w-6 text-amber-600" />;
    }
    if (name.includes('manager') || name.includes('operator')) {
      return <Shield className="h-6 w-6 text-info" />;
    }
    if (name.includes('viewer') || name.includes('read')) {
      return <Eye className="h-6 w-6 text-success" />;
    }
    return <Activity className="h-6 w-6 text-purple-600" />;
  };


const actions = buildRoleActions({
  surface: "list",
  isSystem: !!role.is_system,
  canEdit: !!(canEdit && onEdit),
  canDelete: !!(canDelete && onDelete),
  onView,
  onEdit: onEdit ?? (() => {}),
  onDelete: onDelete ?? (() => {}),
}).filter((a) => a.label !== "View Details"); // Card already navigates on click; hide redundant View

return (
    <GlassCard 
      variant="default"
      hover={false}
      className="cursor-pointer group"
      onClick={onView}
    >
      <GlassCardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg bg-gradient-to-br backdrop-blur-sm border transition-all duration-300 ${
              role.name.toLowerCase().includes('admin') || role.name.toLowerCase().includes('super')
                ? 'from-amber-500/10 to-amber-500/5 border-amber-500/20 group-hover:from-amber-500/20 group-hover:to-amber-500/10'
                : role.name.toLowerCase().includes('manager') || role.name.toLowerCase().includes('operator')
                ? 'from-info/10 to-info/5 border-info/20 group-hover:from-info/20 group-hover:to-info/10'
                : role.name.toLowerCase().includes('viewer') || role.name.toLowerCase().includes('read')
                ? 'from-success/10 to-success/5 border-success/20 group-hover:from-success/20 group-hover:to-success/10'
                : 'from-purple-500/10 to-purple-500/5 border-purple-500/20 group-hover:from-purple-500/20 group-hover:to-purple-500/10'
            }`}>
              {getRoleIcon(role.name)}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">{role.name}</h3>
              {role.is_system && (
                <Badge variant="outline" className="text-xs mt-1">
                  <Lock className="h-3 w-3 mr-1" />
                  System Role
                </Badge>
              )}
            </div>
          </div>
          
          {actions.length > 0 && (
            <div onClick={(e) => e.stopPropagation()}>
              <ActionsDropdown actions={actions} />
            </div>
          )}
        </div>
      </GlassCardHeader>
      
      <GlassCardContent className="pt-0">
        <div className="space-y-4">
          {role.description && (
            <p className="text-sm text-muted-foreground line-clamp-2">
              {role.description}
            </p>
          )}
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Users className="h-4 w-4" />
              <span>{userCount} user{userCount !== 1 ? 's' : ''} assigned</span>
            </div>
          </div>
          
          {/* Permission summary - placeholder for now */}
          <div className="flex flex-wrap gap-1 mt-2">
            <Badge variant="secondary" className="text-xs">
              Basic Access
            </Badge>
            {role.name.toLowerCase().includes('admin') && (
              <>
                <Badge variant="secondary" className="text-xs">
                  User Management
                </Badge>
                <Badge variant="secondary" className="text-xs">
                  System Config
                </Badge>
              </>
            )}
          </div>
        </div>
      </GlassCardContent>
    </GlassCard>
  );
}